/**
 * Library GlideSlider
 * Created 18.05.21
 *
 * @author Christoph Ehlers <ce@construktiv.de> | Construktiv GmbH
 */
import Glide from '@glidejs/glide';

const defaultBreakpointMobile = 680;
const glideDefaults = {
  type: 'carousel',
  perView: 4,
  peekBefore: 0,
  peekAfter: 0
};

export default class GlideSliderProvider {
  _activateSliders() {

    this.sliders.forEach(item => {
      let mount = true;
      let element = item;
      const deviceName = (window.innerWidth <= this.breakpointMobile ? 'Mobile' : 'Desktop');

      if (item.firstChild.dataset && item.firstChild.dataset.disableGlide){
        mount = (item.firstChild.dataset.disableGlide==='true'?false:true);
      }

      if (item.firstChild.dataset && item.firstChild.dataset['disableGlide'+deviceName]){
        mount = (item.firstChild.dataset['disableGlide'+deviceName]==='true'?false:true);
      }
      if(mount){
        item.classList.remove('deaktivated');
      }else{
        item.classList.add('deaktivated');
      }
      if (mount && this._dispatchCallback('sliderWillActivate', item)!==false){
        const glideProps = this._getGlideProps(item);
        let doMount = true;
        if(glideProps.disableGlide && glideProps.disableGlide==='true'){doMount=false;}
        if (glideProps.useChild){
          element = item.childNodes[parseInt(glideProps.useChild)];
          delete glideProps.useChild;
        }
        if(element.classList && element.classList.contains('glide--swipeable')) {
          doMount = false;
        }
        if(doMount){
          glideProps.peek = { before: glideProps.peekBefore, after: glideProps.peekAfter };
          delete glideProps.peekBefore;
          delete glideProps.peekAfter;
          element.querySelectorAll('.js-slide-count-all').forEach((counter)=>{
            counter.parentNode.replaceChild(
              document.createTextNode(element.querySelectorAll('.glide__slide').length),
              counter
            );
          });

          const glide = new Glide(element, glideProps);
          glide.mount();
          this.glides.push(glide);

          this._dispatchCallback('sliderActivated', item);
        }
      }
    });
    this._dispatchCallback('slidersActivated', this.sliders);
    this.slidersEnabled = true;
    this.slidersViewstate = this._getCalcedViewState();
    return this;
  }

  _getGlideProps(item) {
    let result = {};
    let dataset=-1;
    const device = (window.innerWidth <= this.breakpointMobile ? 'mobile' : 'desktop');
    [
      item.firstChild.childNodes[1],
      item.firstChild.childNodes[0],
      item.childNodes[1],
      item.childNodes[0],
      item
    ].map((element)=>{
      if (dataset===-1){
        dataset = (element && element.dataset ? element.dataset : -1);
      }
    });

    [
      'autoplay',
      'focusAt',
      'gap',
      'peekAfter',
      'peekBefore',
      'perView',
      'type',
      'useChild',
      'disableGlide'
    ].map(attributeName=>{
      if (glideDefaults[attributeName]!==undefined) {
        result[attributeName] = glideDefaults[attributeName];
      }

      if (dataset!==-1){
        if (dataset[device+attributeName.charAt(0).toUpperCase()+attributeName.slice(1)]) {
          result[attributeName] = dataset[device+attributeName.charAt(0).toUpperCase()+attributeName.slice(1)];
        } else {
          if (dataset[attributeName]) {
            result[attributeName] = dataset[attributeName];
          }
        }
      }
    });

    for(let index in dataset){
      if (index.indexOf('perViewUntilLower')===0){
        const breakpoint = parseInt(index.substr("perViewUntilLower-".length));
        if(window.innerWidth <= breakpoint){
          result.perView = dataset[index];
        }
      }
    }

    return result;
  }

  _getCalcedViewState(){
    return (window.innerWidth<=this.breakpointMobile?0:1);
  }

  _deaktivateSliders() {
    this.sliders.forEach(item => {
      this._dispatchCallback('sliderWillDeactivate', item);
      const glide = this.glides.pop();
      if (glide){
        glide.destroy();
      }
      this._dispatchCallback('sliderDeactivated', item);
    });
    this._dispatchCallback('slidersDeactivated', this.sliders);
    this.slidersEnabled = false;
    return this;
  }

  _dispatchCallback(name, args) {
    let abort = false;
    if (!this.callbacks[name]){ return null; }

    this.callbacks.any.map(listener => {
      if (!abort){
        if (listener({ name, args })===false){
          abort = true;
        }
      }
    });
    if (!abort){
      this.callbacks[name].map(listener => {
        if (!abort){
          if (listener(args)===false){
            abort = true;
          }
        }
      });
    }

    return !abort;
  }

  addListener(name, callback) {
    if (!this.callbacks[name]) {
      this.callbacks[name] = [];
    }
    this.callbacks[name].push(callback);
    return this;
  }

  constructor(name) {
    this.name = name;
    this.breakpointMobile = defaultBreakpointMobile;
    this.callbacks = {
      any: [],
      sliderWillActivate: [],
      sliderActivated: [],
      slidersActivated: [],
      sliderDeactivated: [],
      slidersDeactivated: []
    };
    this.glides = [];
    this.slidersEnabled = false;
    this.slidersViewstate = -1; // 0 = Mobile / 1 = Desktop
    this.enableSliderMobile = false;
    this.enableSliderDesktop = false;

  }

  execute() {
    if (!this.sliders) {
      return null;
    }
    if (this._getCalcedViewState()!==this.slidersViewstate){
      this._deaktivateSliders();
    }
    if (window.innerWidth <= this.breakpointMobile) {
      // mobile darstellung
      if (this.enableSliderMobile) {
        if (!this.slidersEnabled) {
          this._activateSliders();
        }
      } else {
        if (this.slidersEnabled) {
          this._deaktivateSliders();
        }
      }
    } else {
      // desktop
      if (this.enableSliderDesktop) {
        if (!this.slidersEnabled) {
          this._activateSliders();
        }
      } else {
        if (this.slidersEnabled) {
          this._deaktivateSliders();
        }
      }
    }
  }

  init(selector) {
    this.sliders = document.querySelectorAll(selector);
    window.addEventListener('load', this.execute.bind(this));
    window.addEventListener('resize', this.execute.bind(this));
    return this;
  }

  setEnableSliderDesktop(enable) {
    this.enableSliderDesktop = enable;
    return this;
  }

  setEnableSliderMobile(enable) {
    this.enableSliderMobile = enable;
    return this;
  }
}